// client/src/components/plataforma/planosContas/PlansContasPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContaPlans, useMutateContaPlans } from '../../../hooks/useContaPlans';
import { Package, Plus, Loader2, Edit, Trash2, CheckCircle, XCircle, List, Star, Users, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';

const formatPrice = (price) => {
  if (typeof price !== 'number') return 'N/A';
  return (price / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const PlansContasPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedPlano, setSelectedPlano] = useState(null);

  const {
    data: contasPlans = [],
    isLoading,
    refetch
  } = useContaPlans({
    status: statusFilter === 'all' ? undefined : statusFilter
  });

  const { deletePlan } = useMutateContaPlans();
  const isDeleting = deletePlan.isPending;

  const handleDeactivate = useCallback((plan) => {
    if (!plan._id) {
      console.error('❌ ID do plano não encontrado');
      return;
    }

    const activeAccounts = plan.activeAccountsCount || 0;
    const confirmMessage = activeAccounts > 0 ?
      `Este plano possui ${activeAccounts} conta(s) ativa(s). Deseja realmente desativá-lo?` :
      'Tem certeza que deseja desativar este plano?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    deletePlan.mutate(plan._id, {
      onSuccess: () => refetch()
    });
    setSelectedPlano(null);
  }, [deletePlan, refetch]);

  // Contar features habilitadas
  const countEnabledFeatures = (features = {}) => {
    if (!features) return 0;
    return Object.keys(features)
      .filter(key => key.startsWith('has') && features[key] === true)
      .length;
  };

  // Obter limite de usuários
  const getUserLimit = (features = {}) => {
    if (!features) return 'N/A';

    const userLimit = features.limiteUsuarios;
    if (!userLimit) return 'N/A';

    if (typeof userLimit === 'object') {
      return userLimit.isUnlimited ? 'Ilimitado' : userLimit.value || 0;
    }

    return userLimit;
  };

  const hasPlans = contasPlans.length > 0;

  const actions = selectedPlano ? [
    {
      key: 'contas',
      label: 'Ver contas vinculadas',
      icon: <Users size={20} className="icon" />,
      onClick: () => { navigate(`/plataforma/planos-contas/${selectedPlano._id}/editar`, { state: { openTab: 'contas' } }); setSelectedPlano(null); },
    },
    {
      key: 'editar',
      label: 'Editar plano',
      icon: <Edit size={20} className="icon" />,
      onClick: () => { navigate(`/plataforma/planos-contas/${selectedPlano._id}/editar`); setSelectedPlano(null); },
    },
    ...(selectedPlano.isActive ? [{
      key: 'desativar',
      label: 'Desativar plano',
      icon: <Trash2 size={20} className="icon" />,
      onClick: () => handleDeactivate(selectedPlano),
      destructive: true,
      loading: isDeleting,
    }] : []),
  ] : [];

  return (
    <Fragment>
      <div className="page-management-container">
        <PageHeader
          icon={<Package />}
          title="Planos de Contas"
          subtitle={`${contasPlans.length} plano(s)`}
          actions={
            <>
              <div className="view-switcher-group">
                <button
                  type="button"
                  className={`action-button-icon ${statusFilter === 'active' ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter('active')}
                  title="Ver planos ativos"
                >
                  <CheckCircle size={18} />
                </button>
                <button
                  type="button"
                  className={`action-button-icon ${statusFilter === 'inactive' ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter('inactive')}
                  title="Ver planos inativos"
                >
                  <XCircle size={18} />
                </button>
                <button
                  type="button"
                  className={`action-button-icon ${statusFilter === 'all' ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter('all')}
                  title="Ver todos os planos"
                >
                  <List size={18} />
                </button>
              </div>
            </>
          }
        />
      </div>

      <div className="table-container">
        <div className="table-wrapper">
          {isLoading ? (
            <div className="table-loading-state">
              <Loader2 size={24} className="animate-spin" />
              <span>Carregando planos...</span>
            </div>
          ) : hasPlans ? (
            <table className="styled-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Nome do Plano</th>
                  <th>Preço Mensal</th>
                  <th>Features</th>
                  <th>Limite de Usuários</th>
                  <th>Contas Ativas</th>
                  <th>Teste Grátis</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {contasPlans.map((plano) => {
                  const activeAccountsCount = plano.activeAccountsCount ?? (plano.activeAccounts || []).length;

                  return (
                    <tr key={plano._id} onClick={() => setSelectedPlano(plano)} style={{ cursor: 'pointer' }}>
                      <td className="font-medium">
                        {plano.name}
                        {plano.isDefaultTrial && (
                          <span title="Este é o plano padrão para novos testes" className="ml-2 text-yellow-500 inline-flex align-middle">
                            <Star size={16} fill="currentColor" />
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`font-semibold ${plano.price === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                          {formatPrice(plano.price)}
                        </span>
                        {plano.price === 0 && (
                          <div className="text-xs text-green-600">Grátis</div>
                        )}
                      </td>
                      <td>
                        <div className="text-sm">
                          <span className="font-medium text-gray-900">
                            {countEnabledFeatures(plano.features)}
                          </span>
                          <span className="text-gray-500"> features</span>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm font-medium">
                          {getUserLimit(plano.features)}
                        </span>
                      </td>
                      <td>
                        {activeAccountsCount === 0 ? (
                          <span className="text-gray-400 text-sm">Nenhuma conta</span>
                        ) : (
                          <span className="badge badge-success">{activeAccountsCount} conta{activeAccountsCount !== 1 ? 's' : ''}</span>
                        )}
                      </td>
                      <td>
                        {plano.trialPeriodDays > 0 ? (
                          <span className="badge badge-info">
                            {plano.trialPeriodDays} dias
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${plano.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {plano.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedPlano(plano); }}
                          title="Ações"
                          className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MoreVertical size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="table-empty-state">
              <Package size={48} className="text-gray-400 mx-auto" />
              <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">Nenhum plano encontrado</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                {statusFilter === 'active' ? 'Não há planos ativos no momento.' :
                  statusFilter === 'inactive' ? 'Não há planos inativos no momento.' :
                    'Não há planos cadastrados no sistema.'}
              </p>
              <button
                onClick={() => navigate('/plataforma/planos-contas/novo')}
                className="action-button action-button-primary"
              >
                <Plus size={18} className="mr-2" /> Novo Plano
              </button>
            </div>
          )}
        </div>
      </div>

      <ActionsDrawer
        open={!!selectedPlano}
        onClose={() => setSelectedPlano(null)}
        title={selectedPlano?.name || 'Plano'}
        subtitle={selectedPlano ? formatPrice(selectedPlano.price) : null}
        actions={actions}
      />

      <FabButton
        onClick={() => navigate('/plataforma/planos-contas/novo')}
        title="Novo Plano de Conta"
        visible={true}
        icon={<Package size={26} className="icon" />}
      />
    </Fragment>
  );
};

export default PlansContasPage;
